import * as R from 'ramda';
import * as Dfns from 'date-fns/fp';
import Papa from 'papaparse';
import { capitalizeWords, MedianOrMean } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { decayDataFormatter } from '@Components/Charts/ModelResults/DataFormatters';
import { SatData, DecayData } from '@blisspointmedia/bpm-types/dist/MMM';
import { ModelConfig, TransformedSpendData } from '../hroas-types';
import { equalsIgnoreCase } from '../../../utils/text-utils';

export interface ParsedMediaChannel {
  channel: string | undefined;
  platform: string | undefined;
  tactic: string | undefined;
  brand_nonbrand: string | undefined;
}

export interface EnabledModelWithCsvType {
  id: string;
  csvType: string;
  weight: number;
}

export interface StackedCSVRow {
  refresh_date: string;
  model_type: string;
  model_kpi: string;
  date: string;
  geo: string;
  is_test: string;
  metric: string;
  predicted: number;
  actual: number;
  baseline: number;
  paid_attribution: number;
  media_spend: number;
}

export const MEAN_METRIC = 'mean';

export const TTM_WEEKS = 52;

export const parseMediaChannel = (mediaChannel: string | undefined): ParsedMediaChannel => {
  if (!mediaChannel) {
    return {
      channel: undefined,
      platform: undefined,
      tactic: undefined,
      brand_nonbrand: undefined
    };
  }
  const parts = mediaChannel.split('__');
  return {
    channel: parts[0] || undefined,
    platform: parts[1] || undefined,
    tactic: parts[2] && parts[2] !== 'none' ? parts[2] : undefined,
    brand_nonbrand: parts[3] && parts[3] !== 'none' ? parts[3] : undefined
  };
};

export const parseChannelColumn = (
  channel: string | undefined,
  mediaChannel: string | undefined
): ParsedMediaChannel => {
  if (channel && channel.trim()) {
    const parts = channel
      .split(',')
      .map(p => p.trim())
      .filter(p => p.length > 0);
    if (parts.length > 0) {
      return {
        channel: parts[0] || undefined,
        platform: parts.length > 1 ? parts[1] : undefined,
        tactic: parts.length > 2 ? parts[2] : undefined,
        brand_nonbrand: parts.length > 3 ? parts[3] : undefined
      };
    }
  }

  return parseMediaChannel(mediaChannel);
};

export const parseChannelName = (channel: string | undefined, isBrandEquity: boolean): string => {
  if (!channel) {
    return 'Unknown';
  }

  if (isBrandEquity) {
    const trimmed = channel.trim();

    if (trimmed.startsWith('Commerce')) {
      const parts = trimmed.split(' - ').filter(p => p.trim());
      if (parts.length >= 2) {
        return `${parts[0]} ${parts[1]}`;
      }
      return parts[0] || 'Unknown';
    }

    if (trimmed.startsWith('Youtube')) {
      const parts = trimmed.split(' - ').filter(p => p.trim());
      if (parts.length >= 2) {
        return `${parts[0]} ${parts[1]}`;
      }
      return parts[0] || 'Unknown';
    }

    const firstPart = trimmed.split(' - ')[0]?.trim();
    return firstPart || 'Unknown';
  }

  const firstPart = channel.split(',')[0]?.trim();
  return firstPart || 'Unknown';
};

export const MODEL_TYPE_TOTAL = '__TOTAL__';
export const MODEL_TYPE_TOTAL_NEW = 'Total';

/**
 * Identity signature for a model-toggle set: id + label + appLabel per model, order-independent.
 * Used to gate replacing the toggle set only when its actual identity changes (not just its id
 * set).
 */
export const modelSetSignature = (models: ModelConfig[]): string =>
  models
    .map(model => `${model.id}:${model.label}:${model.appLabel}`)
    .sort()
    .join(',');

export const createPlatformKeyString = (
  platform?: string,
  tactic?: string,
  brandNonbrand?: string
): string => {
  const platformParts = [platform, tactic, brandNonbrand].filter(Boolean);
  return platformParts.length > 0 ? platformParts.join(' - ') : platform || 'All';
};

export const createExpandedPlatformKey = (
  channel: string,
  platform?: string,
  tactic?: string,
  brandNonbrand?: string
): string => {
  const platformKey = createPlatformKeyString(platform, tactic, brandNonbrand);
  return `${channel}__${platformKey}`;
};

export const parseCsv = <T>(csvText: string): Papa.ParseResult<T> =>
  Papa.parse<T>(csvText, {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: true
  });

// Normalizes an "M/d/yyyy" date (mapModelOverviewRows' isoToMddyyyy format) to ISO "yyyy-mm-dd"
// via string parsing, not a `Date` object -- `new Date("yyyy-mm-dd")` parses as UTC midnight but
// `new Date("M/d/yyyy")` as local midnight, so comparing the two formats via `Date` is unreliable
// (DEV-21662). Passes an already-ISO date through unchanged.
export const toIsoDate = (dateStr: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  const [month, day, year] = dateStr.split('/');
  if (!month || !day || !year) {
    return dateStr;
  }
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

export const parseMddyyDate = (dateStr: string): Date | null => {
  try {
    const parsed = Dfns.parse(new Date(), 'M/d/yyyy', dateStr);
    return Dfns.isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const formatDateToMddyy = (date: Date): string => Dfns.format('M/d/yy', date);

export const isTestRow = (row: StackedCSVRow): boolean =>
  String(row.is_test).toUpperCase() === 'TRUE';

export const calculateModelRefreshDate = (
  modelId: string,
  stackedCSVData: StackedCSVRow[]
): string => {
  if (R.isEmpty(stackedCSVData)) {
    return 'N/A';
  }

  const filteredRows = stackedCSVData.filter(
    row => row.model_type === modelId && row.metric === MEAN_METRIC
  );

  if (R.isEmpty(filteredRows)) {
    return 'N/A';
  }

  const refreshDates = filteredRows
    .map(row => parseMddyyDate(String(row.refresh_date)))
    .filter((date): date is Date => date !== null);

  if (R.isEmpty(refreshDates)) {
    return 'N/A';
  }

  const minRefreshDate = new Date(Math.min(...refreshDates.map(d => d.getTime())));
  return formatDateToMddyy(minRefreshDate);
};

export const calculateModelMape = (
  modelId: string,
  stackedCSVData: StackedCSVRow[]
): number | null => {
  if (R.isEmpty(stackedCSVData)) {
    return null;
  }

  const testRows = stackedCSVData.filter(
    row =>
      row.model_type === modelId &&
      row.metric === MEAN_METRIC &&
      isTestRow(row) &&
      Number(row.actual) !== 0
  );

  if (R.isEmpty(testRows)) {
    return null;
  }

  const apes: number[] = [];
  testRows.forEach(row => {
    const actual = Number(row.actual);
    const predicted = Number(row.predicted);

    if (actual !== 0) {
      const ape = Math.abs((actual - predicted) / actual);
      apes.push(ape);
    }
  });

  if (R.isEmpty(apes)) {
    return null;
  }

  const averageMape = apes.reduce((sum, ape) => sum + ape, 0) / apes.length;
  return averageMape * 100;
};

export const formatMapeAsString = (mape: number | null): string =>
  mape !== null ? `${mape.toFixed(1)}%` : 'N/A';

// Render an integer-rounded percent, but show "<1%" / "-<1%" for sub-percent
// non-zero magnitudes so they aren't lost as "0%".
export const formatPercentRounded = (value: number): string => {
  if (value === 0) {
    return '0%';
  }
  if (value > 0 && value < 1) {
    return '<1%';
  }
  if (value < 0 && value > -1) {
    return '-<1%';
  }
  return `${Math.round(value)}%`;
};

// Shape-agnostic "divide each weight by the average active weight" normalizer (so e.g. 1·1·1·1
// gives the same result as 2·2·2·2) — shared by calculateNormalizedWeights below and Scenario
// Planning's normalizeModelWeights (ScenarioPlanningUtils.tsx), which otherwise duplicated this
// same math over a different input/output shape. sum <= 0 means every weight was deliberately
// zeroed out (not "unconfigured" — an empty list already returns {} above) — keep every weight at
// 0 rather than dividing by zero.
export const normalizeWeightsByAverage = (
  weights: { id: string; weight: number }[]
): Record<string, number> => {
  if (weights.length === 0) {
    return {};
  }
  const sum = weights.reduce((s, w) => s + w.weight, 0);
  if (sum <= 0) {
    return Object.fromEntries(weights.map(w => [w.id, 0]));
  }
  const n = weights.length;
  return Object.fromEntries(weights.map(w => [w.id, (w.weight * n) / sum]));
};

export const calculateNormalizedWeights = (
  enabledModelsWithWeights: EnabledModelWithCsvType[]
): Map<string, number> => {
  if (R.isEmpty(enabledModelsWithWeights)) {
    return new Map();
  }

  const normalized = normalizeWeightsByAverage(
    enabledModelsWithWeights.map(m => ({ id: m.csvType, weight: m.weight }))
  );
  return new Map(Object.entries(normalized));
};

export const getRecentWeeksDateSet = (
  rows: StackedCSVRow[],
  weeks: number = TTM_WEEKS
): Set<string> => {
  const dateMap = new Map<string, Date>();
  rows.forEach(row => {
    const dateStr = String(row.date);
    if (dateStr) {
      const parsedDate = parseMddyyDate(dateStr);
      if (parsedDate) {
        dateMap.set(dateStr, parsedDate);
      }
    }
  });

  if (dateMap.size === 0) {
    return new Set();
  }

  const sortedDates = Array.from(dateMap.entries())
    .sort((a, b) => b[1].getTime() - a[1].getTime())
    .slice(0, Math.min(weeks, dateMap.size))
    .map(([dateStr]) => dateStr);

  return new Set(sortedDates);
};

export const getModelTtmRows = (
  modelId: string,
  stackedCSVData: StackedCSVRow[],
  weeks: number = TTM_WEEKS
): StackedCSVRow[] => {
  if (R.isEmpty(stackedCSVData)) {
    return [];
  }

  const modelRows = stackedCSVData.filter(
    row => row.model_type === modelId && row.metric === MEAN_METRIC
  );

  if (R.isEmpty(modelRows)) {
    return [];
  }

  const recentWeeksDateSet = getRecentWeeksDateSet(modelRows, weeks);

  return modelRows.filter(row => {
    const dateStr = String(row.date);
    return recentWeeksDateSet.has(dateStr);
  });
};

export const calculateModelTtmRevenueCredit = (
  modelId: string,
  stackedCSVData: StackedCSVRow[],
  weeks: number = TTM_WEEKS,
  // Brand Equity only (DEV-21662, Taurey Carr) -- see getMdbeInducedEffectByDate. When supplied,
  // replaces BE's own `paid_attribution` with its MDBE induced effect for the same TTM window.
  // Callers pass it for Brand Equity slots only; other slots keep their own `paid_attribution`.
  mdbeInducedEffectByDate?: Map<string, number>
): number | null => {
  const filteredRows = getModelTtmRows(modelId, stackedCSVData, weeks);

  if (R.isEmpty(filteredRows)) {
    return null;
  }

  const predictedRevenue = filteredRows.reduce((sum, row) => sum + (Number(row.predicted) || 0), 0);
  const attribution = mdbeInducedEffectByDate
    ? filteredRows.reduce(
        (sum, row) => sum + (mdbeInducedEffectByDate.get(toIsoDate(String(row.date))) ?? 0),
        0
      )
    : filteredRows.reduce((sum, row) => sum + (Number(row.paid_attribution) || 0), 0);

  if (predictedRevenue === 0) {
    return null;
  }

  return (attribution / predictedRevenue) * 100;
};

export interface SpendEffectShareCsvRow {
  kpi: string;
  model_type: string;
  slotId: string;
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brand_nonbrand: string;
  media_type: string;
  geo: string;
  date: string;
  attribution_type: string;
  estimate_type: string;
  spend: number;
  contribution: number;
  contribution_normalized_to_observed?: number;
  dark_last_quarter?: number;
  // DEV-24605: same <1% logic as dark_last_quarter, but a trailing-twelve-months
  // lookback instead of 13 weeks. Backs the "Omit Dark Inputs" toggle on
  // RoasCompositionByRevenueStream, which is already TTM-scoped.
  dark_last_year?: number;
  // Feature-id output format (client `away`): populated `feature_id` with blank `name`/`channel`.
  // Resolved to a pretty name via the getMmmFeatureNames map in the feature_id-resolution PR.
  feature_id?: string;
  model_scope?: string;
  regional_scope?: string;
}

// Brand Equity's Media-Driven Brand Equity (MDBE) induced effect on real channels, keyed by ISO
// date (DEV-21662). Sums BE's feature-level covariate rows from spend_effect_share.csv, excluding
// the "Covariates" aggregate row -- reconciles to the same total cptbTwoStageSnapData derives
// independently from the twostage decomposition data.
export const getMdbeInducedEffectByDate = (
  spendEffectShareRows: SpendEffectShareCsvRow[]
): Map<string, number> => {
  const map = new Map<string, number>();
  spendEffectShareRows.forEach(row => {
    if (
      row.model_type !== 'brand_equity' ||
      row.media_type !== 'demand_generation' ||
      row.name === 'Covariates' ||
      row.attribution_type !== 'raw' ||
      row.estimate_type !== 'mean'
    ) {
      return;
    }
    const isoDate = toIsoDate(String(row.date));
    map.set(isoDate, (map.get(isoDate) ?? 0) + (Number(row.contribution) || 0));
  });
  return map;
};

export const transformSpendEffectShareCsvToTransformedData = (
  rows: SpendEffectShareCsvRow[],
  enabledModelIds: string[]
): TransformedSpendData[] => {
  if (enabledModelIds.length === 0) {
    return [];
  }

  const filtered = rows.filter(
    r =>
      r.geo === 'national_geo' &&
      r.estimate_type === 'mean' &&
      r.attribution_type === 'raw' &&
      (r.media_type === 'demand_generation' || r.media_type === 'demand_capture') &&
      enabledModelIds.includes(r.slotId)
  );

  const spendByGroup = new Map<string, { row: SpendEffectShareCsvRow; maxSpend: number }>();
  filtered.forEach(r => {
    const key = `${r.name}|||${r.date}`;
    const spend = Number(r.spend) || 0;
    const existing = spendByGroup.get(key);
    if (!existing || spend > existing.maxSpend) {
      spendByGroup.set(key, { row: r, maxSpend: spend });
    }
  });

  const result: TransformedSpendData[] = [];
  spendByGroup.forEach(({ row, maxSpend }) => {
    result.push({
      date: row.date,
      channel: row.channel || 'none',
      platform: row.platform || 'none',
      tactic: row.tactic || 'none',
      brand_nonbrand: row.brand_nonbrand || 'none',
      raw_spend: maxSpend,
      name: row.name
    });
  });

  return result;
};

export interface HolisticRcCsvRow {
  kpi: string;
  model_type: string;
  media_channel: string;
  credible_interval: number;
  weights: number;
  estimate_type: string;
  raw_spend: number;
  actualized_spend: number;
  raw_effect: number;
  actualized_effect: number;
  raw_return_next_dollar: number;
  actualized_return_next_dollar: number;
  previous_qtr_mean_value: number;
  dark_last_quarter?: number;
  // Feature-id output format (client `away`): populated `feature_id` with blank `name`/`channel`.
  // Resolved to a pretty name via the getMmmFeatureNames map in the feature_id-resolution PR.
  feature_id?: string;
  model_scope?: string;
  regional_scope?: string;
}

export interface SaturationDataPoint {
  actualized_spend?: number;
  actualized_effect?: number;
  adstock_spend?: number;
  response?: number;
  ci_lo?: number | null;
  ci_hi?: number | null;
  return_next_dollar?: number;
  mean?: number;
  median?: number;
  name?: string;
  channel?: string;
  platform?: string;
  tactic?: string;
  brand_nonbrand?: string;
  incrementality_test?: boolean;
  dark_last_quarter?: number;
  client?: string;
  kpi?: string;
  media_type?: string;
  refresh_date?: string;
  model_type?: string;
  [key: string]: any;
}

export interface SaturationCurve {
  name: string;
  data: SaturationDataPoint[];
  grouping: { channel: string; platform: string; tactic: string; brand_nonbrand: string };
  incrementality: boolean;
  model_type: string;
}

export interface GroupedLine {
  channel: string;
  platforms: {
    platform: string;
    total: SaturationCurve | null;
    models: SaturationCurve[];
    areas: SaturationCurve[];
  }[];
}

export interface CIConfidenceIntervalKeys {
  ciLoKey: string;
  ciBandKey: string;
  ciHiKey: string;
  ciRangeKey: string;
}

export const generateCIKeys = (lineName: string): CIConfidenceIntervalKeys => ({
  ciLoKey: `${lineName}_ci_lo`,
  ciBandKey: `${lineName}_ci_band`,
  ciHiKey: `${lineName}_ci_hi`,
  ciRangeKey: `${lineName}_ci_range`
});

export const hasValidCIValues = (
  ciLo: number | null | undefined,
  ciHi: number | null | undefined
): boolean => !R.isNil(ciLo) && !R.isNil(ciHi);

export const dataPointHasCI = (
  dp: SaturationDataPoint,
  ciKeys?: CIConfidenceIntervalKeys
): boolean => {
  if (ciKeys) {
    return (
      hasValidCIValues(dp.ci_lo, dp.ci_hi) ||
      hasValidCIValues(
        dp[ciKeys.ciLoKey] as number | null | undefined,
        dp[ciKeys.ciBandKey] as number | null | undefined
      ) ||
      !R.isNil(dp[ciKeys.ciRangeKey])
    );
  }
  return hasValidCIValues(dp.ci_lo, dp.ci_hi);
};

export const transformHolisticRcToSatData = (
  csvData: HolisticRcCsvRow[],
  enabledModelTypes: string[],
  normalizedWeights: Map<string, number>,
  isActualized: boolean,
  kpi: string,
  client = '',
  refreshDate = '',
  brandEquityModelTypes: string[] = []
): SatData[] => {
  if (!csvData || csvData.length === 0) {
    return [];
  }

  const filteredData = csvData.filter(
    row =>
      ['ci_lo', 'mean', 'ci_hi'].includes(row.estimate_type) &&
      enabledModelTypes.includes(row.model_type)
  );

  if (filteredData.length === 0) {
    return [];
  }

  const groupedByModel = new Map<string, HolisticRcCsvRow[]>();

  filteredData.forEach(row => {
    const modelGroupKey = `${row.media_channel}__${row.actualized_spend}__${row.model_type}`;
    if (!groupedByModel.has(modelGroupKey)) {
      groupedByModel.set(modelGroupKey, []);
    }
    groupedByModel.get(modelGroupKey)!.push(row);
  });

  const transformed: SatData[] = [];

  groupedByModel.forEach((rows, _groupKey) => {
    if (rows.length === 0) {
      return;
    }

    const firstRow = rows[0];
    // New CSV format uses comma-separated human-readable names ("Channel, Platform").
    // Old format uses double-underscore keys ("channel__platform__...").
    const isNewFormat = !firstRow.media_channel?.includes('__');
    const parsed = parseChannelColumn(
      isNewFormat ? firstRow.media_channel : undefined,
      firstRow.media_channel
    );
    const rowModelType = firstRow.model_type;

    const spendLevelMap = new Map<
      number,
      {
        spend: number;
        meanEffect: number;
        ciLoEffect: number | null;
        ciHiEffect: number | null;
        previousQtrMeanValue: number;
      }
    >();

    rows.forEach(row => {
      const spend = Number(row.actualized_spend) || 0;
      const effect = Number(row.actualized_effect) || 0;
      const previousQtrMeanValue = Number(row.previous_qtr_mean_value) || 0;
      const estimateType = row.estimate_type;

      if (!spendLevelMap.has(spend)) {
        spendLevelMap.set(spend, {
          spend,
          meanEffect: 0,
          ciLoEffect: null,
          ciHiEffect: null,
          previousQtrMeanValue: 0
        });
      }

      const existing = spendLevelMap.get(spend)!;
      if (estimateType === 'mean') {
        existing.meanEffect += effect;
        existing.previousQtrMeanValue = Math.max(
          existing.previousQtrMeanValue,
          previousQtrMeanValue
        );
      } else if (estimateType === 'ci_lo') {
        existing.ciLoEffect = R.isNil(existing.ciLoEffect) ? effect : existing.ciLoEffect + effect;
      } else if (estimateType === 'ci_hi') {
        existing.ciHiEffect = R.isNil(existing.ciHiEffect) ? effect : existing.ciHiEffect + effect;
      }
    });

    Array.from(spendLevelMap.values()).forEach(
      ({ spend, meanEffect, ciLoEffect, ciHiEffect, previousQtrMeanValue }) => {
        const channelName =
          parseChannelName(parsed.channel, brandEquityModelTypes.includes(rowModelType)) ||
          parsed.channel ||
          'Unknown';
        const nameParts = [
          parsed.channel,
          parsed.platform,
          parsed.tactic,
          parsed.brand_nonbrand
        ].filter(Boolean);
        const name = nameParts.length > 0 ? nameParts.join(', ') : channelName;

        const isMeanPoint = previousQtrMeanValue === 1 ? 1 : 0;

        transformed.push({
          name,
          channel: parsed.channel || '',
          platform: parsed.platform || '',
          tactic: parsed.tactic || '',
          brand_nonbrand: parsed.brand_nonbrand || '',
          actualized_spend: spend,
          actualized_effect: meanEffect,
          adstock_spend: spend, // Ensure adstock_spend is always present for SatData compatibility
          response: meanEffect, // SatData requires response property
          ci_lo: ciLoEffect,
          ci_hi: ciHiEffect,
          return_next_dollar: 0,
          mean: isMeanPoint,
          median: isMeanPoint,
          incrementality_test: false,
          dark_last_quarter: Number(firstRow.dark_last_quarter ?? 0),
          client,
          kpi: firstRow.kpi || kpi,
          media_type: parsed.channel || '',
          refresh_date: refreshDate,
          model_type: rowModelType
        });
      }
    );
  });

  return transformed.sort((a, b) => {
    const aName = a.name || '';
    const bName = b.name || '';
    if (aName !== bName) {
      return aName.localeCompare(bName);
    }
    return (a.actualized_spend || 0) - (b.actualized_spend || 0);
  });
};

export const hroasSaturationDataFormatter = (
  data: SaturationDataPoint[] | SatData[],
  disabledPlatform: boolean,
  centralMeasure: MedianOrMean,
  companyEqualsTest?: boolean,
  enabledModelTypes: string[] = [],
  normalizedWeights: Map<string, number> = new Map()
): {
  saturationCurves: SaturationCurve[];
  centralMeasures: Record<
    string,
    { mean?: number | null; median?: number | null; dataPoint: number | null }
  >;
  groupedLines: GroupedLine[];
} => {
  if (!data || data.length === 0) {
    return { saturationCurves: [], centralMeasures: {}, groupedLines: [] };
  }

  const filteredData = (data as (SaturationDataPoint | SatData)[]).filter(
    item => item.model_type !== MODEL_TYPE_TOTAL
  );

  const groupedByNameAndModelType = R.groupBy(
    item => `${(item as any).name}__${(item as any).model_type}`,
    filteredData
  );

  const transformedData: SaturationCurve[] = R.pipe(
    R.toPairs,
    R.map(([, groupData]: [string, SaturationDataPoint[]]) => {
      const firstItem = groupData[0];
      const channel = capitalizeWords(firstItem.channel ?? '', disabledPlatform);
      const rawPlatform = firstItem.platform ?? '';
      let platform = capitalizeWords(rawPlatform, disabledPlatform);
      const tactic = capitalizeWords(firstItem.tactic ?? '', disabledPlatform);
      const brand_nonbrand = capitalizeWords(firstItem.brand_nonbrand ?? '', disabledPlatform);
      if (
        (rawPlatform && rawPlatform.toLowerCase() === 'none') ||
        ((!platform || disabledPlatform) && !brand_nonbrand && !tactic)
      ) {
        platform = 'Total';
      }

      const incrementality = Boolean(firstItem.incrementality_test);
      const modelType = firstItem.model_type || MODEL_TYPE_TOTAL;
      const baseName = capitalizeWords(firstItem.name ?? '', disabledPlatform);

      const uniqueName =
        modelType === MODEL_TYPE_TOTAL
          ? `${baseName}__${MODEL_TYPE_TOTAL}`
          : `${baseName}__${modelType}`;

      const spendLevelMap = new Map<number, any>();
      groupData.forEach(dataPoint => {
        const spend = dataPoint.actualized_spend || dataPoint.adstock_spend || 0;
        const effect = dataPoint.actualized_effect || dataPoint.response || 0;
        const ciLo = dataPoint.ci_lo;
        const ciHi = dataPoint.ci_hi;

        if (spendLevelMap.has(spend)) {
          const existing = spendLevelMap.get(spend)!;
          existing.actualized_effect = (existing.actualized_effect || 0) + effect;
          existing.return_next_dollar =
            (existing.return_next_dollar || 0) + (dataPoint.return_next_dollar || 0);
          existing.mean = Math.max(existing.mean || 0, dataPoint.mean || 0);
          existing.median = Math.max(existing.median || 0, dataPoint.median || 0);
          if (!R.isNil(ciLo)) {
            existing.ci_lo = R.isNil(existing.ci_lo) ? ciLo : existing.ci_lo + ciLo;
          }
          if (!R.isNil(ciHi)) {
            existing.ci_hi = R.isNil(existing.ci_hi) ? ciHi : existing.ci_hi + ciHi;
          }
        } else {
          spendLevelMap.set(spend, {
            ...dataPoint,
            actualized_spend: spend,
            actualized_effect: effect,
            ci_lo: !R.isNil(ciLo) ? ciLo : null,
            ci_hi: !R.isNil(ciHi) ? ciHi : null
          });
        }
      });

      const combinedData = Array.from(spendLevelMap.values())
        .map(dataPoint => ({
          ...dataPoint,
          name: uniqueName
        }))
        .sort(
          (a, b) =>
            (a.actualized_spend || a.adstock_spend || 0) -
            (b.actualized_spend || b.adstock_spend || 0)
        );

      return {
        name: uniqueName,
        data: combinedData,
        grouping: { channel, platform, tactic, brand_nonbrand },
        incrementality,
        model_type: modelType
      };
    })
  )(groupedByNameAndModelType);

  const centralMeasures: any = {};
  transformedData.forEach(item => {
    const centralRow = item.data.find(row => row[centralMeasure.toLowerCase()] === 1);
    const spendValue = centralRow
      ? centralRow.actualized_spend || centralRow.adstock_spend || 0
      : null;
    centralMeasures[item.name] = centralRow
      ? {
          [centralMeasure.toLowerCase()]: spendValue,
          dataPoint: spendValue
        }
      : { [centralMeasure.toLowerCase()]: null, dataPoint: null };
  });

  const totalLines: SaturationCurve[] = [];
  if (enabledModelTypes.length > 0 && normalizedWeights.size > 0) {
    const platformGroups = new Map<string, SaturationCurve[]>();

    transformedData.forEach(item => {
      if (!enabledModelTypes.includes(item.model_type)) {
        return;
      }

      const { channel, platform, tactic, brand_nonbrand } = item.grouping;
      const fullPlatformKey = createExpandedPlatformKey(channel, platform, tactic, brand_nonbrand);

      if (!platformGroups.has(fullPlatformKey)) {
        platformGroups.set(fullPlatformKey, []);
      }
      platformGroups.get(fullPlatformKey)!.push(item);
    });

    platformGroups.forEach((models, _fullPlatformKey) => {
      if (models.length === 0) {
        return;
      }

      const firstModel = models[0];
      const { channel, platform, tactic, brand_nonbrand } = firstModel.grouping;

      const allModelsHaveIncrementality = models.every(model => model.incrementality);

      const sortedModelData = models.map(model => ({
        ...model,
        sortedData: [...model.data].sort(
          (a, b) =>
            (a.actualized_spend || a.adstock_spend || 0) -
            (b.actualized_spend || b.adstock_spend || 0)
        )
      }));

      const maxDataPoints = Math.max(...sortedModelData.map(model => model.sortedData.length));

      const spendLevelMap = new Map<
        number,
        {
          spend: number;
          weightedEffect: number;
          mean: number;
          median: number;
          returnNextDollar: number;
        }
      >();

      for (let i = 0; i < maxDataPoints; i++) {
        const spendsAtPosition: number[] = [];
        let weightedEffect = 0;
        let mean = 0;
        let median = 0;
        let returnNextDollar = 0;

        sortedModelData.forEach(model => {
          if (i < model.sortedData.length) {
            const dataPoint = model.sortedData[i];
            const spend = dataPoint.actualized_spend || dataPoint.adstock_spend || 0;
            const effect = dataPoint.actualized_effect || dataPoint.response || 0;
            const weight = normalizedWeights.get(model.model_type) || 1.0;

            spendsAtPosition.push(spend);
            weightedEffect += effect * weight;
            mean = Math.max(mean || 0, dataPoint.mean || 0);
            median = Math.max(median || 0, dataPoint.median || 0);
            returnNextDollar += (dataPoint.return_next_dollar || 0) * weight;
          }
        });

        if (spendsAtPosition.length > 0) {
          const maxSpend = Math.max(...spendsAtPosition);
          spendLevelMap.set(maxSpend, {
            spend: maxSpend,
            weightedEffect,
            mean,
            median,
            returnNextDollar
          });
        }
      }

      const totalData = Array.from(spendLevelMap.values())
        .map(({ spend, weightedEffect, mean, median, returnNextDollar }) => ({
          actualized_spend: spend,
          actualized_effect: weightedEffect,
          return_next_dollar: returnNextDollar,
          mean,
          median,
          incrementality_test: allModelsHaveIncrementality,
          dark_last_quarter: 0
        }))
        .sort((a, b) => (a.actualized_spend || 0) - (b.actualized_spend || 0));

      const nameParts = [channel, platform, tactic, brand_nonbrand].filter(Boolean);
      const baseName = nameParts.length > 0 ? nameParts.join(', ') : channel;
      const totalName = `${baseName}__Total`;

      totalLines.push({
        name: totalName,
        data: totalData.map(dataPoint => ({
          ...dataPoint,
          name: totalName
        })),
        grouping: { channel, platform, tactic, brand_nonbrand },
        incrementality: allModelsHaveIncrementality,
        model_type: MODEL_TYPE_TOTAL_NEW
      });

      const totalCentralRow = totalData.find(row => row[centralMeasure.toLowerCase()] === 1);
      const totalSpendValue = totalCentralRow ? totalCentralRow.actualized_spend || 0 : null;
      centralMeasures[totalName] = totalCentralRow
        ? {
            [centralMeasure.toLowerCase()]: totalSpendValue,
            dataPoint: totalSpendValue
          }
        : { [centralMeasure.toLowerCase()]: null, dataPoint: null };
    });
  }

  const allSaturationCurves = [...transformedData, ...totalLines];

  const channelGroups = new Map<
    string,
    Map<string, { total: SaturationCurve | null; models: SaturationCurve[] }>
  >();

  allSaturationCurves.forEach(item => {
    const { channel, platform, tactic, brand_nonbrand } = item.grouping;

    const platformKey = createPlatformKeyString(platform, tactic, brand_nonbrand);

    if (!channelGroups.has(channel)) {
      channelGroups.set(channel, new Map());
    }

    const platformGroups = channelGroups.get(channel)!;
    if (!platformGroups.has(platformKey)) {
      platformGroups.set(platformKey, { total: null, models: [] });
    }

    const platformGroup = platformGroups.get(platformKey)!;
    platformGroup.models.push(item);
  });

  const groupedLines = Array.from(channelGroups.entries()).map(([channel, platformGroups]) => {
    const platforms = Array.from(platformGroups.entries())
      .filter(([platformKey]) => {
        const lowerPlatformKey = platformKey.toLowerCase();
        const lowerChannel = channel.toLowerCase();
        return !(lowerPlatformKey === `all ${lowerChannel}` || lowerPlatformKey === 'all');
      })
      .map(([platformKey, { models }]) => ({
        platform: platformKey,
        total: null,
        models,
        areas: models
      }));

    return {
      channel,
      platforms: R.sortBy(R.compose(R.toLower, R.prop('platform')))(platforms)
    };
  });

  return {
    saturationCurves: allSaturationCurves,
    centralMeasures,
    groupedLines: R.sortBy(R.compose(R.toLower, R.prop('channel')))(groupedLines)
  };
};

export interface HolisticDecayCsvRow {
  client: string;
  model: string;
  period: number;
  name: string;
  spend_carryover: number;
  max_lag: number;
  dark_last_quarter: number;
}

/** Shared type for decay structure from transformHolisticDecayCsvToDecayData. */
export type DecayStructure = Record<
  string,
  { groups: Record<string, { models: Record<string, number[]> }> }
> | null;

export const transformHolisticDecayCsvToDecayData = (
  csvData: HolisticDecayCsvRow[],
  slotIdByDecayKey: Record<string, string>
): DecayStructure => {
  if (!csvData || csvData.length === 0) {
    return null;
  }

  const modelTypeMap: Record<string, string> = { ...slotIdByDecayKey, holistic: 'Total' };

  if (csvData.length === 0) {
    return null;
  }

  const parseName = (name: string): { channel: string; group: string } => {
    const hasComma = name.includes(',');
    if (hasComma) {
      const parts = name.split(',').map(p => p.trim());
      return {
        channel: parts[0] || '',
        group: name
      };
    }
    return {
      channel: name,
      group: 'none'
    };
  };

  const structure: Record<
    string,
    {
      groups: Record<
        string,
        {
          models: Record<string, number[]>;
        }
      >;
    }
  > = {};

  const rowGroups = new Map<string, HolisticDecayCsvRow[]>();

  csvData.forEach(row => {
    const modelType = modelTypeMap[row.model] || row.model;
    const { channel, group } = parseName(row.name);

    const key = `${channel}|||${group}|||${modelType}`;

    if (!rowGroups.has(key)) {
      rowGroups.set(key, []);
    }
    rowGroups.get(key)!.push(row);
  });

  rowGroups.forEach((rows, key) => {
    const [channel, group, modelType] = key.split('|||');

    if (!structure[channel]) {
      structure[channel] = { groups: {} };
    }

    if (!structure[channel].groups[group]) {
      structure[channel].groups[group] = { models: {} };
    }

    const sortedRows = [...rows].sort((a, b) => a.period - b.period);

    const spendCarryoverValues = sortedRows.map(row => row.spend_carryover);

    structure[channel].groups[group].models[modelType] = spendCarryoverValues;
  });

  return structure;
};

/**
 * Transforms CSV data into DecayData format for use with decayDataFormatter
 *
 * @param csvData - The CSV data from holistic decay CSV
 * @returns Array of DecayData objects matching the DecayData type from @blisspointmedia/bpm-types
 */
export const transformCsvToDecayDataArray = (
  csvData: HolisticDecayCsvRow[],
  slotIdByDecayKey: Record<string, string>
): {
  client: string;
  kpi: string;
  refresh_date: string;
  period: number;
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brand_nonbrand: string;
  incrementality_test: boolean;
  spend_carryover: number;
  max_lag: number;
  dark_last_quarter: number;
}[] => {
  const modelTypeMap: Record<string, string> = { ...slotIdByDecayKey, holistic: 'Total' };

  const modelToKpiMap: Record<string, string> = {
    instore_sales: 'instore_sales',
    instore_revenue: 'instore_revenue',
    online_revenue: 'online_revenue',
    online_sales: 'online_sales',
    brand_equity: 'online_revenue',
    total_revenue: 'online_revenue',
    holistic: 'online_revenue'
  };

  const parseName = (
    name: string
  ): {
    channel: string;
    platform: string | null;
    tactic: string | null;
    brand_nonbrand: string | null;
  } => {
    const parts = name.split(',').map(p => p.trim());
    if (parts.length === 1) {
      return {
        channel: parts[0] || '',
        platform: null,
        tactic: null,
        brand_nonbrand: null
      };
    }
    if (parts.length === 2) {
      return {
        channel: parts[0] || '',
        platform: parts[1] || null,
        tactic: null,
        brand_nonbrand: null
      };
    }
    if (parts.length === 3) {
      const brandNonbrand = parts[2] || null;
      return {
        channel: parts[0] || '',
        platform: parts[1] || null,
        tactic: null,
        brand_nonbrand: brandNonbrand
      };
    }
    return {
      channel: parts[0] || '',
      platform: parts.length > 1 ? parts[1] : null,
      tactic: parts.length > 2 ? parts[2] : null,
      brand_nonbrand: parts.length > 3 ? parts[3] : null
    };
  };

  const refreshDate =
    csvData.length > 0 && (csvData[0] as any).refresh_date
      ? (csvData[0] as any).refresh_date
      : new Date().toISOString().split('T')[0];

  return csvData.map(row => {
    const modelType = modelTypeMap[row.model] || row.model;
    const { channel, platform, tactic, brand_nonbrand } = parseName(row.name);
    const nameWithModel = `${row.name}__${modelType}`;
    const kpi = modelToKpiMap[row.model] || 'online_revenue';

    return {
      client: row.client,
      kpi,
      refresh_date: refreshDate,
      period: row.period,
      name: nameWithModel,
      channel,
      platform: platform || '',
      tactic: tactic || '',
      brand_nonbrand: brand_nonbrand || '',
      incrementality_test: false,
      spend_carryover: row.spend_carryover,
      max_lag: row.max_lag,
      dark_last_quarter: row.dark_last_quarter
    };
  });
};

/**
 * Transforms decay data structure into GroupedArea format suitable for buildLegendByGroupedArea
 *
 * @param decayStructure - The decay data structure from transformHolisticDecayCsvToDecayData
 * Format: { channel: { groups: { groupName: { models: { modelType: number[] } } } } }
 * @param disabledPlatform - Whether platform is disabled
 * @returns Array of GroupedArea objects matching the format expected by buildLegendByGroupedArea
 */
export const transformDecayStructureToGroupedArea = (
  decayStructure: Record<
    string,
    {
      groups: Record<
        string,
        {
          models: Record<string, number[]>;
        }
      >;
    }
  >,
  disabledPlatform: boolean,
  enabledModelTypes: string[] = [],
  getModelLabel: (modelType: string) => string = modelType => modelType
): {
  channel: string;
  platforms: {
    platform: string;
    total: any | null;
    models: {
      name: string;
      data: any[];
      grouping: { channel: string; platform: string; tactic: string; brand_nonbrand: string };
      incrementality: boolean;
      model_type: string;
    }[];
    areas: {
      name: string;
      data: any[];
      grouping: { channel: string; platform: string; tactic: string; brand_nonbrand: string };
      incrementality: boolean;
      model_type: string;
    }[];
  }[];
}[] => {
  const channelGroups = new Map<string, Map<string, { total: any | null; models: any[] }>>();

  Object.entries(decayStructure).forEach(([rawChannel, channelData]) => {
    const channel = capitalizeWords(rawChannel, disabledPlatform);

    Object.entries(channelData.groups).forEach(([groupName, groupData]) => {
      const groupParts = groupName.split(',').map(part => part.trim());
      const groupChannel = groupParts[0] || '';
      const platformName = groupParts.length > 1 ? groupParts[1] : '';
      const brandNonbrandRaw = groupParts.length > 2 ? groupParts[2] : '';

      const isChannelWithoutPlatform =
        equalsIgnoreCase(groupName, 'none') ||
        (equalsIgnoreCase(groupChannel, rawChannel) && !platformName);

      let platform: string;
      if (isChannelWithoutPlatform) {
        platform = 'Average';
      } else {
        if (!equalsIgnoreCase(groupChannel, rawChannel) || !platformName) {
          return;
        }
        platform = capitalizeWords(platformName, disabledPlatform);
      }

      let brandNonbrand = '';
      if (brandNonbrandRaw) {
        brandNonbrand = capitalizeWords(brandNonbrandRaw, disabledPlatform);
      }

      const platformKey = createPlatformKeyString(platform, undefined, brandNonbrand || undefined);

      if (!channelGroups.has(channel)) {
        channelGroups.set(channel, new Map());
      }

      const platformGroups = channelGroups.get(channel)!;

      if (!platformGroups.has(platformKey)) {
        platformGroups.set(platformKey, { total: null, models: [] });
      }

      const platformGroup = platformGroups.get(platformKey)!;

      Object.entries(groupData.models).forEach(([modelType, decayValues]) => {
        if (
          enabledModelTypes.length > 0 &&
          !enabledModelTypes.includes(modelType) &&
          modelType !== MODEL_TYPE_TOTAL_NEW
        ) {
          return;
        }

        let baseName: string;
        if (isChannelWithoutPlatform) {
          baseName = channel;
        } else {
          baseName = brandNonbrand
            ? `${channel}, ${platform}, ${brandNonbrandRaw}`
            : `${channel}, ${platform}`;
        }
        const name = `${baseName}__${modelType}`;

        const data: any[] = decayValues.map((value, index) => ({
          period: index + 1,
          spend_carryover: value
        }));

        const saturationCurve = {
          name,
          data,
          grouping: {
            channel,
            platform,
            tactic: '',
            brand_nonbrand: brandNonbrand
          },
          incrementality: false,
          model_type: modelType
        };

        platformGroup.models.push(saturationCurve);
      });
    });
  });

  const groupedLines = Array.from(channelGroups.entries()).map(([channel, platformGroups]) => {
    const platforms = Array.from(platformGroups.entries())
      .filter(([platformKey]) => {
        const lowerPlatformKey = platformKey.toLowerCase();
        const lowerChannel = channel.toLowerCase();
        return !(lowerPlatformKey === `all ${lowerChannel}` || lowerPlatformKey === 'all');
      })
      .map(([platformKey, { models }]) => ({
        platform: platformKey,
        total: null,
        models: R.sortBy((model: any) => {
          if (model.model_type === MODEL_TYPE_TOTAL_NEW) {
            return '0';
          }
          return getModelLabel(model.model_type);
        })(models),
        areas: R.sortBy((model: any) => {
          if (model.model_type === MODEL_TYPE_TOTAL_NEW) {
            return '0';
          }
          return getModelLabel(model.model_type);
        })(models)
      }));

    return {
      channel,
      platforms: R.sortBy(R.compose(R.toLower, R.prop('platform')))(platforms)
    };
  });

  return R.sortBy(R.compose(R.toLower, R.prop('channel')))(groupedLines);
};

export const calculateEffectShareWeights = (
  satData: SatData[],
  enabledModelTypes: string[] = []
): Record<string, number> => {
  const effectShareWeights: Record<string, number> = {};

  if (!satData || satData.length === 0) {
    return effectShareWeights;
  }

  const filteredSatData =
    enabledModelTypes.length > 0
      ? satData.filter(item => item.model_type && enabledModelTypes.includes(item.model_type))
      : satData;

  const channelGroups: Record<string, Record<string, number>> = {};

  filteredSatData.forEach(item => {
    const channel = item.channel || '';
    const platform = item.platform || '';
    const tactic = item.tactic || '';
    const brandNonbrand = item.brand_nonbrand || '';
    const modelType = item.model_type || '';
    const effect = item.actualized_effect || item.response || 0;

    if (!modelType || effect <= 0) {
      return;
    }

    const platformKey = createPlatformKeyString(
      platform || undefined,
      tactic || undefined,
      brandNonbrand || undefined
    );
    const channelKey = `${channel}__${platformKey}`;

    if (!channelGroups[channelKey]) {
      channelGroups[channelKey] = {};
    }

    const modelEffects = channelGroups[channelKey];
    const currentEffect = modelEffects[modelType] || 0;
    modelEffects[modelType] = currentEffect + effect;
  });

  Object.entries(channelGroups).forEach(([channelKey, modelEffects]) => {
    let totalChannelEffect = 0;
    Object.values(modelEffects).forEach(effect => {
      totalChannelEffect += effect;
    });

    if (totalChannelEffect > 0) {
      Object.entries(modelEffects).forEach(([modelType, effect]) => {
        const effectW = effect / totalChannelEffect;
        const weightKey = `${channelKey}__${modelType}`;
        effectShareWeights[weightKey] = effectW;
      });
    }
  });

  return effectShareWeights;
};

export const combineEffectShareAndUserWeights = (
  effectShareWeights: Record<string, number>,
  normalizedWeights: Record<string, number>,
  enabledModelTypes: string[] = []
): Record<string, number> => {
  const combinedWeights: Record<string, number> = {};

  if (Object.keys(effectShareWeights).length === 0 || Object.keys(normalizedWeights).length === 0) {
    return combinedWeights;
  }

  const channelWeightProducts: Record<string, Record<string, number>> = {};

  Object.entries(effectShareWeights).forEach(([weightKey, effectW]) => {
    const parts = weightKey.split('__');
    if (parts.length < 3) {
      return;
    }

    const modelType = parts[parts.length - 1];
    const channelKey = parts.slice(0, -1).join('__');

    if (enabledModelTypes.length > 0 && !enabledModelTypes.includes(modelType)) {
      return;
    }

    const userW = normalizedWeights[modelType] || 1.0;
    const wProd = effectW * userW;

    if (!channelWeightProducts[channelKey]) {
      channelWeightProducts[channelKey] = {};
    }

    channelWeightProducts[channelKey][weightKey] = wProd;
  });

  Object.entries(channelWeightProducts).forEach(([, weightProducts]) => {
    let totWProd = 0;
    Object.values(weightProducts).forEach(wProd => {
      totWProd += wProd;
    });

    if (totWProd > 0) {
      Object.entries(weightProducts).forEach(([weightKey]) => {
        const parts = weightKey.split('__');
        const modelType = parts[parts.length - 1];
        const effectW = effectShareWeights[weightKey] || 0;
        const userW = normalizedWeights[modelType] || 1.0;

        const finalW = (userW * effectW) / totWProd;
        combinedWeights[weightKey] = finalW;
      });
    }
  });

  return combinedWeights;
};

export interface ActualizedCsvRow {
  model_type: string;
  model_kpi?: string;
  metric: string;
  media_channel?: string;
  channel?: string;
  time?: string;
  date?: string;
  actualized_spend?: number | null;
  actualized_effect?: number | null;
  media_type?: 'demand_generation' | 'demand_capture';
  refresh_date?: string;
}

/**
 * Calculate effect share weights from CSV data (DM_actualized_spend_effect.csv)
 * This matches the Python prep_effect_data function approach - from Catherine Hayden
 *
 * @param csvData - Array of CSV rows with effect data
 * @param enabledModelTypes - Array of enabled model types to filter by
 * @returns Map of weight keys (channelKey__modelType) to effect weights
 */
export const calculateEffectShareWeightsFromCsv = (
  csvData: ActualizedCsvRow[],
  enabledModelTypes: string[] = []
): Record<string, number> => {
  const effectShareWeights: Record<string, number> = {};

  if (!csvData || csvData.length === 0) {
    return effectShareWeights;
  }

  const filteredData = csvData.filter(row => {
    if (row.metric !== 'mean') {
      return false;
    }
    if (enabledModelTypes.length > 0) {
      return enabledModelTypes.includes(row.model_type);
    }
    return true;
  });

  if (filteredData.length === 0) {
    return effectShareWeights;
  }

  const channelModelEffects: Record<string, Record<string, number>> = {};

  filteredData.forEach(row => {
    const channelName = row.channel || '';
    const modelType = row.model_type || '';
    const effect = row.actualized_effect || 0;

    if (!channelName || !modelType || effect <= 0) {
      return;
    }

    const parsed = parseChannelColumn(channelName, row.media_channel);
    const { channel, platform, tactic, brand_nonbrand: brandNonbrand } = parsed;

    if (!channel) {
      return;
    }

    const platformKey = createPlatformKeyString(platform, tactic, brandNonbrand);
    const channelKey = `${channel}__${platformKey}`;

    if (!channelModelEffects[channelKey]) {
      channelModelEffects[channelKey] = {};
    }

    const modelEffects = channelModelEffects[channelKey];
    const currentEffect = modelEffects[modelType] || 0;
    modelEffects[modelType] = currentEffect + effect;
  });

  Object.entries(channelModelEffects).forEach(([channelKey, modelEffects]) => {
    let totalChannelEffect = 0;
    Object.values(modelEffects).forEach(effect => {
      totalChannelEffect += effect;
    });

    if (totalChannelEffect > 0) {
      Object.entries(modelEffects).forEach(([modelType, effect]) => {
        const effectW = effect / totalChannelEffect;
        const weightKey = `${channelKey}__${modelType}`;
        effectShareWeights[weightKey] = effectW;
      });
    }
  });

  return effectShareWeights;
};

interface HroasDecayDataFormatterResult {
  newD: Record<string, string | number>[];
  lines: {
    dataKey: string;
    grouping?: { channel: string; platform: string; tactic: string; brand_nonbrand: string } | null;
    incrementality: boolean;
    name: string;
  }[];
  groupedLines: {
    channel: string;
    areas: {
      dataKey: string;
      grouping?: {
        channel: string;
        platform: string;
        tactic: string;
        brand_nonbrand: string;
      } | null;
      incrementality: boolean;
      name: string;
    }[];
  }[];
}

export const hroasDecayDataFormatter = (
  data: DecayData[],
  yAxisDataKey: string,
  disabledPlatform: boolean,
  companyEqualsTest?: boolean
): HroasDecayDataFormatterResult => {
  const formatted = decayDataFormatter(data, yAxisDataKey, disabledPlatform, companyEqualsTest);

  // decayDataFormatter is typed against bpm-types (DateChartRow / AreaGrouping),
  // whose `newD` values and grouping fields (platform/tactic/brand_nonbrand) are
  // nullable. HROAS consumers treat these as non-null; the runtime shape is
  // identical, so narrow the type here without touching the data.
  return formatted as HroasDecayDataFormatterResult;
};
